import { newId } from "@engram/core/id";
import { Extension, getHTMLFromFragment, getText, getTextSerializersFromSchema, type Editor as TEditor } from "@tiptap/core";
import { Color, FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TableKit } from "@tiptap/extension-table";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { redoDepth, undoDepth } from "@tiptap/pm/history";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlignCenter, AlignLeft, AlignRight, Baseline, Bold, Camera, CheckSquare, FileSymlink, ChevronDown, ChevronRightSquare, Code, Eraser, Highlighter, IndentDecrease, IndentIncrease, Italic, Link2,
  List, ListOrdered, Lock, Mic, PanelTop, Plus, Redo2, RemoveFormatting, Search, Strikethrough, Subscript as SubIcon, Superscript as SupIcon, Underline, Undo2, CircleCheck, MoreHorizontal,
} from "lucide-react";
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { db } from "../../db/client";
import { sha256Hex } from "../attachments";
import { useLayout } from "../layout";
import { Menu, Popover, Prompt, pointAnchor, useAnchor, type MenuEntry } from "../Menu";
import { AttachmentImage, EngramLink, EngramTable, FastTableCell, FastTableHeader, FileAttachment, TaskRef } from "./nodes";
import { SearchHighlight } from "./search";
import { COMMANDS, InlineTagSuggest, NoteMention, SlashCommand, filterCommands, formatDate, formatTime, type EditorEnv } from "./suggest";
import { BlockHandle } from "./BlockHandle";
import { Callout, DateMention, InlineTag, PageBreak, TableOfContents } from "./blocks";
import { TableControls } from "./TableControls";
import { insertNewTask } from "./tasks";
import { Folds } from "./folds";
import { isWebUrl, linkAtCaret, linkToCard, RichLink } from "./richlink";
import { MathBlock, MermaidBlock } from "./diagrams";
import { recordAudio } from "./recorder";
import { Encrypted, encryptSelection } from "./encrypt";

export interface EditorHandle {
  editor: TEditor | null;
}

const MAX_FILE = 95_000_000; // under the Workers free-plan request body limit (100 MB), with room for headers

/**
 * Phones: how much of the note's scrolling area the floating pill toolbar covers (0 when it isn't showing). ProseMirror
 * only keeps the caret inside the scrolling area, so the line being typed slid under the pill (owner, 17 Oct).
 */
function coveredByToolbar(dom: Element): number {
  const scroller = dom.closest(".editor-scroll");
  const bar = dom.closest(".editor-pane")?.querySelector(".toolbar.phone");
  if (!scroller || !bar) return 0;
  const b = bar.getBoundingClientRect();
  return b.height ? Math.max(0, scroller.getBoundingClientRect().bottom - b.top) : 0;
}

/** Evernote keyboard shortcuts that TipTap doesn't bind by default (help.evernote.com "Keyboard shortcuts"). */
const EvernoteKeys = Extension.create<{ env: EditorEnv | null }>({
  name: "evernoteKeys",
  priority: 1000, // beats StarterKit's Mod-e (inline code) and TextAlign's Mod-Shift-l
  addOptions() {
    return { env: null };
  },
  addKeyboardShortcuts() {
    const c = () => this.editor.chain().focus();
    return {
      "Mod-Shift-b": () => this.editor.commands.toggleBulletList(),
      "Mod-Shift-o": () => this.editor.commands.toggleOrderedList(),
      "Mod-Shift-c": () => this.editor.commands.toggleTaskList(),
      "Mod-Shift-l": () => this.editor.commands.toggleCodeBlock(),
      "Mod-Shift--": () => this.editor.commands.setHorizontalRule(),
      "Alt-Shift-d": () => this.editor.commands.insertContent(formatDate()),
      "Mod-Shift-d": () => this.editor.commands.insertContent(formatTime()),
      // The @ menu opens only after a space or at a line start, so add a space after a word.
      "Mod-Alt-k": () => {
        const { $from } = this.editor.state.selection;
        const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 1), $from.parentOffset, "\0", "\0");
        return this.editor.commands.insertContent(before && !/\s/.test(before) ? " @" : "@");
      },
      "Mod-k": () => {
        window.dispatchEvent(new CustomEvent("engram:link"));
        return true;
      },
      "Mod-Shift-k": () => c().extendMarkRange("link").unsetLink().run(),
      "Mod-t": () => this.editor.commands.toggleStrike(), // Chrome keeps Ctrl+T; works in the Android app
      "Mod-l": () => c().setTextAlign("left").run(),
      "Mod-e": () => c().setTextAlign("center").run(),
      "Mod-j": () => c().setTextAlign("right").run(),
      "Mod-Space": () => c().unsetColor().unsetHighlight().unsetTextAlign().run(), // Simplify formatting
      "Alt-t": () => {
        const env = this.options.env;
        if (env) void insertNewTask(this.editor, env.noteId);
        return true;
      },
    };
  },
});

const MAX_INDENT = 8;

/** Indents (or outdents) the paragraphs and headings in the selection, like Evernote's Tab. */
export function shiftIndent(editor: TEditor, delta: number): boolean {
  const { state, view } = editor;
  const { from, to } = state.selection;
  const tr = state.tr;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name !== "paragraph" && node.type.name !== "heading") return true;
    const cur = Number(node.attrs.indent) || 0;
    const next = Math.max(0, Math.min(MAX_INDENT, cur + delta));
    if (next !== cur) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
    return false;
  });
  if (tr.docChanged) view.dispatch(tr);
  return true;
}

/** List items indent with Tab (TipTap); everything else indents too, so Tab never leaves the note. */
export function indentOrSink(editor: TEditor, delta: 1 | -1): boolean {
  const item = editor.isActive("taskItem") ? "taskItem" : editor.isActive("listItem") ? "listItem" : null;
  if (item) {
    if (delta > 0) editor.chain().focus().sinkListItem(item).run();
    else editor.chain().focus().liftListItem(item).run();
    return true;
  }
  return shiftIndent(editor, delta);
}

const Indent = Extension.create({
  name: "indent",
  // Below lists, tables and the "/" and "@" menus, so their own Tab handling wins.
  priority: 50,
  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const d = Number(el.getAttribute("data-indent"));
              if (d) return Math.min(MAX_INDENT, d);
              const m = /(?:margin|padding)-left:\s*([\d.]+)(px|em)/.exec(el.getAttribute("style") ?? "");
              if (!m) return 0;
              const px = m[2] === "em" ? Number(m[1]) * 16 : Number(m[1]);
              return Math.min(MAX_INDENT, Math.round(px / 32));
            },
            renderHTML: (a) => (a.indent ? { "data-indent": a.indent, style: `margin-left: ${a.indent * 2}em` } : {}),
          },
        },
      },
    ];
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const e = this.editor;
        if (e.isActive("codeBlock")) return e.commands.insertContent("\t");
        if (e.isActive("table")) return false; // next cell
        return indentOrSink(e, 1);
      },
      "Shift-Tab": () => {
        const e = this.editor;
        if (e.isActive("table") || e.isActive("codeBlock")) return e.isActive("codeBlock");
        return indentOrSink(e, -1);
      },
    };
  },
});

export async function insertFiles(editor: TEditor, noteId: string, files: File[], pos?: number) {
  const now = Date.now();
  const nodes = [];
  for (const file of files) {
    if (file.size > MAX_FILE) {
      alert(`${file.name} is larger than 95 MB and can't be attached.`);
      continue;
    }
    const sha = await sha256Hex(file);
    await db.addFile(sha, file);
    let width: number | null = null;
    let height: number | null = null;
    const isImage = /^image\/(png|jpeg|gif|webp|avif|bmp)$/.test(file.type);
    if (isImage) {
      try {
        const bmp = await createImageBitmap(file);
        width = bmp.width;
        height = bmp.height;
        bmp.close();
      } catch {
        /* not decodable; keep as file */
      }
    }
    const id = newId();
    await db.mutate([
      { t: "attachments", id, set: { note_id: noteId, sha256: sha, filename: file.name || "file", mime: file.type || "application/octet-stream", size: file.size, width, height, created: now } },
    ]);
    nodes.push(isImage && width ? { type: "image", attrs: { attachment: id, alt: file.name } } : { type: "fileAttachment", attrs: { attachment: id, name: file.name } });
  }
  if (!nodes.length) return;
  if (pos != null) editor.chain().focus().insertContentAt(pos, nodes).run();
  else editor.chain().focus().insertContent(nodes).run();
  caretAfterBlock(editor);
}

/**
 * Adds a file to the end of a note whose editor isn't open (a voice recording stopped after leaving the note):
 * same attachment row and placeholder the editor would make.
 */
export async function appendFileToNote(noteId: string, file: File) {
  const sha = await sha256Hex(file);
  await db.addFile(sha, file);
  const id = newId();
  const now = Date.now();
  const note = await db.get<{ body_html: string; body_text: string }>("SELECT body_html, body_text FROM notes WHERE id = ? AND deleted = 0", [noteId]);
  if (!note) return;
  const name = (file.name || "file").replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  await db.mutate([
    { t: "attachments", id, set: { note_id: noteId, sha256: sha, filename: file.name || "file", mime: file.type || "application/octet-stream", size: file.size, width: null, height: null, created: now } },
    { t: "notes", id: noteId, set: { body_html: `${note.body_html}<div class="attachment" data-attachment="${id}">${name}</div>`, body_text: note.body_text, updated: now } },
  ]);
}

/** Files waiting for a note's editor to open (a new note made from files: Files page, drop on the note list). */
const queued = new Map<string, File[]>();
export function queueFilesForNote(noteId: string, files: File[]) {
  queued.set(noteId, files);
}

/**
 * After inserting a block atom (image, file, task) ProseMirror leaves it node-selected, so the next
 * keystroke or paste would replace it. Put the caret on a line after it instead, like Evernote.
 */
export function caretAfterBlock(editor: TEditor) {
  const { selection, doc } = editor.state;
  if (!(selection instanceof NodeSelection) || !selection.node.isBlock) return;
  const after = selection.to;
  const next = doc.resolve(after).nodeAfter;
  if (next?.isTextblock) editor.commands.setTextSelection(after + 1);
  else editor.chain().insertContentAt(after, { type: "paragraph" }).setTextSelection(after + 1).run();
}


/*
 * React blocks (tasks, images, files, callouts, date chips…) can only be drawn once <EditorContent> has mounted;
 * those made before (every block of a note being opened) are empty placeholder <span>s. TipTap swaps them by
 * rebuilding every node view when it mounts (fresh functions, so ProseMirror throws the whole note away and draws
 * it again: the 2,080-cell table opened in ~430 ms, ~250 ms without, chat 10). Here only the placeholders are
 * redrawn: a node decoration on each makes ProseMirror ask it to update, it refuses, and ProseMirror builds just
 * that block again, now with React. (Chat 10's version skipped the rebuild unless a React block was already drawn,
 * which is never the case, so from 10 Oct until chat 15 no task, image or file showed inside a note.)
 */
function isPlaceholder(dom: Node | null): boolean {
  return dom instanceof HTMLSpanElement && !dom.className && !dom.firstChild;
}
function redrawPlaceholdersOnly(editor: TEditor | null) {
  const ed = editor as (TEditor & { __redrawOnly?: true }) | null;
  if (!ed || ed.__redrawOnly) return;
  ed.__redrawOnly = true;
  ed.createNodeViews = () => {
    if (ed.isDestroyed) return;
    const { view } = ed;
    const withViews = new Set(Object.keys(view.props.nodeViews ?? {}));
    const decos: Decoration[] = [];
    view.state.doc.descendants((node, pos) => {
      if (!withViews.has(node.type.name) || !isPlaceholder(view.nodeDOM(pos))) return true;
      decos.push(Decoration.node(pos, pos + node.nodeSize, {}, { redraw: true }));
      return false; // a placeholder's content isn't drawn; it comes with the new block
    });
    if (!decos.length) return;
    const set = DecorationSet.create(view.state.doc, decos);
    view.setProps({ decorations: () => set });
    view.setProps({ decorations: undefined });
  };
}

export function Editor({
  noteId,
  initialHtml,
  editable,
  terms,
  collapsible = false,
  onChange,
  onFocusChange,
  toolbarSlot,
  ref,
}: {
  noteId: string;
  initialHtml: string;
  editable: boolean;
  terms: string[];
  /** Evernote's "Collapsible sections": headings get a fold chevron. */
  collapsible?: boolean;
  /** Where the formatting toolbar goes (the row above the title); inline when absent. */
  toolbarSlot?: HTMLElement | null;
  /** Called on every edit with a getter: serializing a big note takes tens of ms, so it runs only when saving. */
  onChange: (body: () => { html: string; text: string }) => void;
  onFocusChange?: (focused: boolean) => void;
  ref?: Ref<EditorHandle>;
}) {
  const [, navigate] = useLocation();
  const fileInput = useRef<HTMLInputElement>(null);
  const env = useMemo<EditorEnv>(
    () => ({
      noteId,
      pickFiles: (accept = "") => {
        if (!fileInput.current) return;
        fileInput.current.accept = accept;
        fileInput.current.click();
      },
    }),
    [noteId],
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // ProseMirror reads these on every scroll to the caret; set just before from the pill toolbar's overlap, so the
  // line being typed stays a line's gap above the pill (scrolls once it gets within 8 px of it).
  const scrollEdges = useRef({ threshold: { top: 0, right: 0, bottom: 0, left: 0 }, margin: { top: 5, right: 5, bottom: 5, left: 5 } }).current;

  const editor = useEditor(
    {
      editable,
      content: initialHtml,
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, link: false, trailingNode: false }),
        EngramLink,
        TaskList,
        TaskItem.configure({ nested: true }),
        TableKit.configure({ table: false, tableCell: false, tableHeader: false }),
        EngramTable,
        Callout,
        PageBreak,
        TableOfContents,
        DateMention,
        InlineTag,
        // Evernote's Toggle: a line that folds the blocks under it; stays open or closed with the note.
        Details.configure({ persist: true, HTMLAttributes: { class: "toggle" } }),
        DetailsSummary,
        DetailsContent,
        FastTableCell,
        FastTableHeader,
        Highlight.configure({ multicolor: true }),
        TextStyle,
        Color,
        FontFamily,
        FontSize,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Subscript,
        Superscript,
        Placeholder.configure({
          // Evernote: an empty note invites writing; an empty line in a note offers the "/" menu.
          placeholder: ({ editor: e }) => (e.isEmpty ? "Start writing or drag files here" : 'Press "/" to insert an element'),
          showOnlyCurrent: true,
          includeChildren: false,
        }),
        AttachmentImage,
        FileAttachment,
        TaskRef,
        SearchHighlight,
        SlashCommand.configure({ env }),
        NoteMention.configure({ env }),
        InlineTagSuggest.configure({ env }),
        EvernoteKeys.configure({ env }),
        Indent,
        Folds.configure({ noteId, enabled: collapsible }),
        RichLink,
        MathBlock,
        MermaidBlock,
        Encrypted,
      ],
      editorProps: {
        attributes: { class: "note-body", spellcheck: "true" },
        scrollThreshold: scrollEdges.threshold,
        scrollMargin: scrollEdges.margin,
        handleScrollToSelection(view) {
          const covered = coveredByToolbar(view.dom);
          scrollEdges.threshold.bottom = covered && covered + 8;
          scrollEdges.margin.bottom = covered ? covered + 24 : 5;
          return false; // ProseMirror scrolls, with these edges
        },
        handleClick(view, _pos, event) {
          // An inline tag opens that tag's notes, like a note link opens its note.
          const tag = (event.target as HTMLElement).closest("[data-inline-tag]")?.getAttribute("data-inline-tag");
          if (tag) {
            event.preventDefault();
            navigate(`/tag/${tag}`);
            return true;
          }
          const a = (event.target as HTMLElement).closest("a");
          if (!a) return false;
          const noteLink = a.getAttribute("data-note-link");
          event.preventDefault();
          if (noteLink) navigate(`/note/${noteLink}`);
          else if (a.href && (!view.editable || event.ctrlKey || event.metaKey)) window.open(a.href, "_blank", "noopener,noreferrer");
          return true;
        },
        handleDOMEvents: {
          // Clicking under a note that ends in a table, image, file or task adds a line there to keep writing
          // (Evernote). Not done on open, so opening a note never edits it.
          mousedown(view, event) {
            if (event.target !== view.dom || !view.editable) return false;
            const last = view.state.doc.lastChild;
            const lastDom = view.dom.lastElementChild;
            if (!last || last.isTextblock || !lastDom || event.clientY < lastDom.getBoundingClientRect().bottom) return false;
            const end = view.state.doc.content.size;
            const tr = view.state.tr.insert(end, view.state.schema.nodes.paragraph!.create());
            tr.setSelection(TextSelection.create(tr.doc, end + 1));
            view.dispatch(tr.scrollIntoView());
            view.focus();
            event.preventDefault();
            return true;
          },
          dblclick(_view, event) {
            const a = (event.target as HTMLElement).closest("a");
            if (a?.href && !a.getAttribute("data-note-link")) window.open(a.href, "_blank", "noopener,noreferrer");
            return false;
          },
        },
        handlePaste(view, event) {
          const files = [...(event.clipboardData?.files ?? [])];
          const text = event.clipboardData?.getData("text/plain").trim() ?? "";
          // A pasted URL becomes a link, with Evernote's offer to show it as a card instead.
          if (!files.length && isWebUrl(text) && view.state.selection.empty && !view.state.selection.$from.parent.type.spec.code) {
            event.preventDefault();
            const from = view.state.selection.from;
            const link = view.state.schema.marks.link!.create({ href: text });
            view.dispatch(view.state.tr.insert(from, view.state.schema.text(text, [link])).scrollIntoView());
            setOffer({ from, to: from + text.length, url: text });
            return true;
          }
          if (!files.length) return false;
          // Excel, Word and Google Sheets put a picture of the selection next to its HTML: paste the HTML (the
          // real table or text). Only a paste that is just files, or HTML that is just one image, inserts files.
          const html = event.clipboardData?.getData("text/html") ?? "";
          if (html && !/^\s*(<meta[^>]*>\s*)?<img\b[^>]*>\s*$/i.test(html.replace(/<!--[\s\S]*?-->/g, "").replace(/<\/?(html|body|head)[^>]*>/gi, ""))) return false;
          event.preventDefault();
          if (editorRef.current) void insertFiles(editorRef.current, noteId, files);
          return true;
        },
        handleDrop(view, event, _slice, moved) {
          if (moved) return false;
          const files = [...(event.dataTransfer?.files ?? [])];
          if (!files.length) return false;
          event.preventDefault();
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          if (editorRef.current) void insertFiles(editorRef.current, noteId, files, pos);
          return true;
        },
      },
      onUpdate: ({ editor, transaction }) => {
        // Only real edits count. Plugins that normalize the doc after a meta-only
        // transaction must not mark a note as edited just because it was opened.
        if (!transaction.docChanged) return;
        // Capture the doc and schema now: the editor may be destroyed (schema cleared) before the save runs.
        const { doc } = editor.state;
        const { schema } = editor;
        onChangeRef.current(() => ({
          html: getHTMLFromFragment(doc.content, schema),
          text: getText(doc, { blockSeparator: "\n", textSerializers: getTextSerializersFromSchema(schema) }).normalize("NFC"),
        }));
      },
      // Startup marks (tools/bench); the phone's offscreen warm-up editor (Warmup.tsx) doesn't count.
      onBeforeCreate: () => noteId !== "warmup" && performance.mark("engram:editor-start"),
      onCreate: () => noteId !== "warmup" && performance.mark("engram:editor"),
      onFocus: () => onFocusChange?.(true),
      onBlur: () => onFocusChange?.(false),
    },
    [noteId],
  );
  redrawPlaceholdersOnly(editor);
  const editorRef = useRef<TEditor | null>(null);
  editorRef.current = editor;
  const [offer, setOffer] = useState<{ from: number; to: number; url: string } | null>(null);
  // The offer goes away once the note changes some other way (typing on, undo…).
  useEffect(() => {
    if (!editor || !offer) return;
    let first = true;
    const onTr = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (first) return void (first = false);
      if (transaction.docChanged) setOffer(null);
    };
    editor.on("transaction", onTr);
    return () => void editor.off("transaction", onTr);
  }, [editor, offer]);

  useImperativeHandle(ref, () => ({ editor }), [editor]);

  useEffect(() => {
    editor?.commands.setFoldsEnabled(collapsible);
  }, [editor, collapsible]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  // Phones: the keyboard coming up shrinks the window, and the browser only brings the caret above the keyboard,
  // not above the pill toolbar that appears with it.
  useEffect(() => {
    if (!editor) return;
    const onResize = () => {
      if (editor.isDestroyed || !editor.isFocused || !coveredByToolbar(editor.view.dom)) return;
      editor.view.dispatch(editor.state.tr.scrollIntoView());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [editor]);

  useEffect(() => {
    const files = editor ? queued.get(noteId) : undefined;
    if (!editor || !files) return;
    queued.delete(noteId);
    void insertFiles(editor, noteId, files);
  }, [editor, noteId]);

  // A task moved to another note (task dialog) leaves this note's body.
  useEffect(() => {
    if (!editor) return;
    const onMoved = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      const { tr } = editor.state;
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "taskRef" && node.attrs.id === id) tr.delete(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize));
      });
      if (tr.docChanged && !editor.isDestroyed) editor.view.dispatch(tr);
    };
    window.addEventListener("engram:task-moved", onMoved);
    return () => window.removeEventListener("engram:task-moved", onMoved);
  }, [editor]);

  useEffect(() => {
    editor?.commands.setSearch(terms);
  }, [editor, terms.join("\u0000")]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {editable && editor && (toolbarSlot ? createPortal(<Toolbar editor={editor} env={env} />, toolbarSlot) : <Toolbar editor={editor} env={env} />)}
      <EditorContent editor={editor} className="editor-content" />
      {editable && editor && <TableControls editor={editor} />}
      {editable && editor && <BlockHandle editor={editor} />}
      {editor && offer && <PasteOffer editor={editor} offer={offer} onClose={() => setOffer(null)} />}
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (editor && files.length) void insertFiles(editor, noteId, files);
        }}
      />
    </>
  );
}

/** After pasting a URL: keep it a link, or show it as a card (Evernote's paste menu). */
function PasteOffer({ editor, offer, onClose }: { editor: TEditor; offer: { from: number; to: number; url: string }; onClose: () => void }) {
  const anchor = useMemo(() => {
    const c = editor.view.coordsAtPos(Math.min(offer.to, editor.state.doc.content.size));
    return pointAnchor(c.left, c.bottom);
  }, [editor, offer]);
  return (
    <Popover anchor={anchor} onClose={onClose} className="menu paste-offer">
      <div className="menu-heading">Paste as</div>
      <button className="menu-item active" onClick={() => (onClose(), editor.commands.focus())}>
        <span className="menu-icon"><Link2 size={14} /></span>
        <span className="menu-label">Link</span>
      </button>
      <button
        className="menu-item"
        onClick={() => {
          onClose();
          void linkToCard(editor, offer.from, offer.to, offer.url);
        }}
      >
        <span className="menu-icon"><PanelTop size={14} /></span>
        <span className="menu-label">Card</span>
      </button>
    </Popover>
  );
}

/** Evernote's font families, mapped to fonts every device has. Sans Serif is the note's own font (Inter). */
const FONTS: [string, string | null][] = [
  ["Sans Serif", null],
  ["Serif", 'Georgia, "Times New Roman", serif'],
  ["Slab Serif", 'Rockwell, "Roboto Slab", "Zilla Slab", Georgia, serif'],
  ["Monospace", "ui-monospace, SFMono-Regular, Consolas, monospace"],
  ["Script", '"Segoe Script", "Brush Script MT", "Snell Roundhand", cursive'],
  ["Handwritten", '"Segoe Print", "Bradley Hand", "Comic Sans MS", cursive'],
];
const SIZES = [8, 9, 10, 12, 14, 15, 16, 18, 20, 24, 30, 36, 48, 64, 72, 96];

/** Evernote's Insert menu: a search box over the grouped elements. */
function InsertMenu({ anchor, editor, env, onClose }: { anchor: HTMLElement; editor: TEditor; env: EditorEnv; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const list = filterCommands(q);
  const run = (cmd: (typeof COMMANDS)[number]) => {
    onClose();
    const at = editor.state.selection.from;
    cmd.run(editor, { from: at, to: at }, env);
  };
  return (
    <Popover anchor={anchor} onClose={onClose} className="menu insert-menu">
      <label className="find-input insert-find">
        <Search size={14} />
        <input
          autoFocus
          placeholder="Search insert options…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setHi(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHi((h) => Math.min(h + 1, list.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHi((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter" && list[hi]) {
              e.preventDefault();
              run(list[hi]);
            }
          }}
        />
      </label>
      <div className="insert-list">
        {list.map((cmd, i) => [
          !q.trim() && cmd.group !== list[i - 1]?.group ? <div key={`g:${cmd.group}`} className="menu-heading">{cmd.group}</div> : null,
          <button key={cmd.title} className={`menu-item${i === hi ? " active" : ""}`} onMouseEnter={() => setHi(i)} onClick={() => run(cmd)}>
            <span className="menu-icon">{cmd.icon}</span>
            <span className="menu-label">{cmd.title}</span>
            {cmd.hint && <span className="menu-hint">{cmd.hint}</span>}
          </button>,
        ])}
        {list.length === 0 && <div className="menu-empty">No matching elements</div>}
      </div>
    </Popover>
  );
}

/** Toolbar items that move into More when the toolbar is too narrow, in Evernote's order. */
const OPTIONAL = ["sep1", "color", "bold", "italic", "underline", "highlight", "sep2", "bullet", "ordered", "checklist", "link"] as const;

const TEXT_COLORS = ["#D14D41", "#DA702C", "#C69B1F", "#4C9A52", "#3A87C8", "#8B6BC9", "#C45C9A", "#8A8580"];
const HIGHLIGHTS = ["#FFEF9E", "#FFD5B8", "#C9EFC4", "#C6E2FF", "#E6D5FF", "#FFD1E3"];

function Toolbar({ editor, env }: { editor: TEditor; env: EditorEnv }) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      task: e.isActive("taskList"),
      link: e.isActive("link"),
      code: e.isActive("code"),
      h: [1, 2, 3, 4].find((l) => e.isActive("heading", { level: l })) ?? 0,
      font: (e.getAttributes("textStyle").fontFamily as string | undefined) ?? null,
      size: (e.getAttributes("textStyle").fontSize as string | undefined) ?? null,
      codeBlock: e.isActive("codeBlock"),
      // History depth, not can().undo(): that dry-runs a command chain on every keystroke.
      canUndo: undoDepth(e.state) > 0,
      canRedo: redoDepth(e.state) > 0,
    }),
  });
  const insert = useAnchor();
  const style = useAnchor();
  const color = useAnchor();
  const more = useAnchor();
  const [linkPrompt, setLinkPrompt] = useState(false);

  useEffect(() => {
    const open = () => setLinkPrompt(true);
    window.addEventListener("engram:link", open);
    return () => window.removeEventListener("engram:link", open);
  }, []);

  const c = () => editor.chain().focus();
  const styleLabel = s.codeBlock ? "Code" : s.h === 1 ? "Large header" : s.h === 2 ? "Medium header" : s.h === 3 ? "Small header" : s.h === 4 ? "Extra small header" : "Normal text";
  const fontLabel = FONTS.find(([, css]) => css === s.font)?.[0] ?? "Sans Serif";
  const sizeLabel = s.size ? s.size.replace(/px$/, "") : String(s.h ? { 1: 24, 2: 20, 3: 17, 4: 15 }[s.h] : 15);
  const font = useAnchor();
  const size = useAnchor();

  /*
   * Evernote keeps the toolbar on one line: formatting buttons that don't fit move to the top of More, in
   * the toolbar's order. Phones keep the sideways-scrolling pill instead.
   */
  const layout = useLayout();
  const barRef = useRef<HTMLDivElement>(null);
  const optWidths = useRef<Record<string, number>>({});
  const [fit, setFit] = useState<number>(OPTIONAL.length);
  const shown = layout === "wide" ? fit : OPTIONAL.length;
  const fitToolbar = useCallback(() => {
    const bar = barRef.current;
    // Hidden until editing starts (Evernote shows "Edited …" there): nothing to measure yet. Checked by class, not by
    // size: reading a size here forced a layout of the whole note right after it opened (~150 ms for a big table).
    if (!bar || layout !== "wide" || !bar.closest(".editor-chrome.editing") || !bar.clientWidth) return;
    const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
    let fixed = 0;
    for (const el of Array.from(bar.children) as HTMLElement[]) {
      const cs = getComputedStyle(el);
      const w = el.getBoundingClientRect().width + parseFloat(cs.marginLeft) + parseFloat(cs.marginRight) + gap;
      if (el.dataset.opt) optWidths.current[el.dataset.opt] = w;
      else fixed += w;
    }
    const avail = bar.clientWidth - fixed;
    let used = 0;
    let n = 0;
    for (const k of OPTIONAL) {
      used += optWidths.current[k] ?? 32;
      if (used > avail) break;
      n++;
    }
    while (n > 0 && OPTIONAL[n - 1]!.startsWith("sep")) n--; // no dangling separator
    setFit(n);
  }, [layout]);
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const ro = new ResizeObserver(fitToolbar);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [fitToolbar]);
  // The style/font/size labels change width as the caret moves.
  // Re-measured after each change too: buttons that just came back have real widths now.
  useLayoutEffect(fitToolbar, [fitToolbar, styleLabel, fontLabel, sizeLabel, fit]);
  const on = (k: (typeof OPTIONAL)[number]) => OPTIONAL.indexOf(k) < shown;
  const moreAnchor = useRef<HTMLElement | null>(null);
  const phone = layout !== "wide";
  const lists = useAnchor();
  const align = useAnchor();
  // "@" and "#" menus need a space (or the line start) before them.
  const trigger = (ch: string) => {
    const $p = editor.state.selection.$from;
    const prev = $p.parent.textBetween(Math.max(0, $p.parentOffset - 1), $p.parentOffset);
    c().insertContent(prev && prev !== " " ? ` ${ch}` : ch).run();
  };
  const overflow = ([
    !on("color") && { label: "Font color", icon: <Baseline size={14} />, onSelect: () => moreAnchor.current && color.open({ currentTarget: moreAnchor.current }) },
    !on("bold") && { label: "Bold", icon: <Bold size={14} />, checked: s.bold, hint: "Ctrl+B", onSelect: () => c().toggleBold().run() },
    !on("italic") && { label: "Italic", icon: <Italic size={14} />, checked: s.italic, hint: "Ctrl+I", onSelect: () => c().toggleItalic().run() },
    !on("underline") && { label: "Underline", icon: <Underline size={14} />, checked: s.underline, hint: "Ctrl+U", onSelect: () => c().toggleUnderline().run() },
    !on("highlight") && { label: "Highlight", icon: <Highlighter size={14} />, hint: "Ctrl+Shift+H", onSelect: () => c().toggleHighlight({ color: HIGHLIGHTS[0]! }).run() },
    !on("bullet") && { label: "Bulleted list", icon: <List size={14} />, checked: s.bullet, hint: "Ctrl+Shift+B", onSelect: () => c().toggleBulletList().run() },
    !on("ordered") && { label: "Numbered list", icon: <ListOrdered size={14} />, checked: s.ordered, hint: "Ctrl+Shift+O", onSelect: () => c().toggleOrderedList().run() },
    !on("checklist") && { label: "Checklist", icon: <CheckSquare size={14} />, checked: s.task, hint: "Ctrl+Shift+C", onSelect: () => c().toggleTaskList().run() },
    { label: "Toggle", icon: <ChevronRightSquare size={14} />, onSelect: () => c().setDetails().updateAttributes("details", { open: true }).run() },
    !on("link") && { label: "Insert link", icon: <Link2 size={14} />, checked: s.link, hint: "Ctrl+K", onSelect: () => setLinkPrompt(true) },
    s.link && {
      label: "Show link as card",
      icon: <PanelTop size={14} />,
      onSelect: () => {
        const l = linkAtCaret(editor);
        if (l) void linkToCard(editor, l.from, l.to, l.url);
      },
    },
  ] as (MenuEntry | false)[]).filter((x): x is MenuEntry => !!x);

  return (
    <div className={`toolbar${phone ? " phone" : ""}`} ref={barRef} onMouseDown={(e) => (e.target as HTMLElement).closest("button") && e.preventDefault()}>
      {/* Evernote Android's pill: + insert, camera, note link, task | list type, outdent, indent, Aa, align | B, I, U… (undo/redo live in the top bar). */}
      {phone && (
        <>
          <button className="tb-insert round" onClick={insert.open} title="Insert" aria-label="Insert">
            <Plus size={18} strokeWidth={2.5} />
          </button>
          <TB title="Camera or photo" onClick={() => env.pickFiles("image/*")}><Camera size={18} /></TB>
          <TB title="Record audio" onClick={() => recordAudio(editor, env.noteId)}><Mic size={18} /></TB>
          <TB title="Link to note" onClick={() => trigger("@")}><FileSymlink size={18} /></TB>
          <TB title="New task" onClick={() => void insertNewTask(editor, env.noteId)}><CircleCheck size={18} className="ic-task" /></TB>
          <span className="tb-sep" />
          <TB title="List type" active={s.bullet || s.ordered || s.task} onClick={lists.open}>{s.ordered ? <ListOrdered size={18} /> : s.task ? <CheckSquare size={18} /> : <List size={18} />}</TB>
          <TB title="Outdent" onClick={() => (editor.commands.focus(), indentOrSink(editor, -1))}><IndentDecrease size={18} /></TB>
          <TB title="Indent" onClick={() => (editor.commands.focus(), indentOrSink(editor, 1))}><IndentIncrease size={18} /></TB>
          <TB title="Text style" active={!!s.h || s.codeBlock} onClick={style.open}><span className="tb-aa">Aa</span></TB>
          <TB title="Alignment" onClick={align.open}><AlignLeft size={18} /></TB>
          <span className="tb-sep" />
          <TB title="Bold" active={s.bold} onClick={() => c().toggleBold().run()}><Bold size={18} /></TB>
          <TB title="Italic" active={s.italic} onClick={() => c().toggleItalic().run()}><Italic size={18} /></TB>
          <TB title="Underline" active={s.underline} onClick={() => c().toggleUnderline().run()}><Underline size={18} /></TB>
          <TB title="Strikethrough" active={s.strike} onClick={() => c().toggleStrike().run()}><Strikethrough size={18} /></TB>
          <TB title="Font color and highlight" onClick={color.open}><Baseline size={18} /></TB>
          <TB title="Highlight" onClick={() => c().toggleHighlight({ color: HIGHLIGHTS[0]! }).run()}><Highlighter size={18} /></TB>
          <TB title="Insert link" active={s.link} onClick={() => setLinkPrompt(true)}><Link2 size={18} /></TB>
          <TB title="More" onClick={(e) => ((moreAnchor.current = e.currentTarget), more.open(e))}><MoreHorizontal size={18} /></TB>
        </>
      )}
      {!phone && (
        <>
          <button className="tb-insert" onClick={insert.open}>
            <Plus size={15} strokeWidth={2.5} /> Insert <ChevronDown size={13} />
          </button>
          <TB title="New task (Alt+T)" onClick={() => void insertNewTask(editor, env.noteId)}><CircleCheck size={16} className="ic-task" /></TB>
          <span className="tb-sep" />
          <TB title="Undo (Ctrl+Z)" disabled={!s.canUndo} onClick={() => c().undo().run()}><Undo2 size={16} /></TB>
          <TB title="Redo (Ctrl+Y)" disabled={!s.canRedo} onClick={() => c().redo().run()}><Redo2 size={16} /></TB>
          <span className="tb-sep" />
          <button className="tb-dropdown" onClick={style.open}>
            {styleLabel} <ChevronDown size={13} />
          </button>
          <button className="tb-dropdown" title="Font" onClick={font.open}>
            {fontLabel} <ChevronDown size={13} />
          </button>
          <button className="tb-dropdown tb-size" title="Font size" onClick={size.open}>
            {sizeLabel} <ChevronDown size={13} />
          </button>
          {on("sep1") && <span className="tb-sep" data-opt="sep1" />}
          {on("color") && <TB opt="color" title="Font color and highlight" onClick={color.open}><Baseline size={16} /></TB>}
          {on("bold") && <TB opt="bold" title="Bold (Ctrl+B)" active={s.bold} onClick={() => c().toggleBold().run()}><Bold size={16} /></TB>}
          {on("italic") && <TB opt="italic" title="Italic (Ctrl+I)" active={s.italic} onClick={() => c().toggleItalic().run()}><Italic size={16} /></TB>}
          {on("underline") && <TB opt="underline" title="Underline (Ctrl+U)" active={s.underline} onClick={() => c().toggleUnderline().run()}><Underline size={16} /></TB>}
          {on("highlight") && <TB opt="highlight" title="Highlight (Ctrl+Shift+H)" onClick={() => c().toggleHighlight({ color: HIGHLIGHTS[0]! }).run()}><Highlighter size={16} /></TB>}
          {on("sep2") && <span className="tb-sep" data-opt="sep2" />}
          {on("bullet") && <TB opt="bullet" title="Bulleted list (Ctrl+Shift+B)" active={s.bullet} onClick={() => c().toggleBulletList().run()}><List size={16} /></TB>}
          {on("ordered") && <TB opt="ordered" title="Numbered list (Ctrl+Shift+O)" active={s.ordered} onClick={() => c().toggleOrderedList().run()}><ListOrdered size={16} /></TB>}
          {on("checklist") && <TB opt="checklist" title="Checklist (Ctrl+Shift+C)" active={s.task} onClick={() => c().toggleTaskList().run()}><CheckSquare size={16} /></TB>}
          {on("link") && <TB opt="link" title="Insert link (Ctrl+K)" active={s.link} onClick={() => setLinkPrompt(true)}><Link2 size={16} /></TB>}
          <TB
            title="More"
            onClick={(e) => {
              moreAnchor.current = e.currentTarget;
              more.open(e);
            }}
          >
            <MoreHorizontal size={16} />
          </TB>
        </>
      )}

      {lists.anchor && (
        <Menu
          anchor={lists.anchor}
          side="top"
          onClose={lists.close}
          entries={[
            { label: "Bulleted list", icon: <List size={14} />, checked: s.bullet, onSelect: () => c().toggleBulletList().run() },
            { label: "Numbered list", icon: <ListOrdered size={14} />, checked: s.ordered, onSelect: () => c().toggleOrderedList().run() },
            { label: "Checklist", icon: <CheckSquare size={14} />, checked: s.task, onSelect: () => c().toggleTaskList().run() },
            { label: "Toggle", icon: <ChevronRightSquare size={14} />, onSelect: () => c().setDetails().updateAttributes("details", { open: true }).run() },
          ]}
        />
      )}
      {align.anchor && (
        <Menu
          anchor={align.anchor}
          side="top"
          onClose={align.close}
          entries={[
            { label: "Left-align", icon: <AlignLeft size={14} />, onSelect: () => c().setTextAlign("left").run() },
            { label: "Center-align", icon: <AlignCenter size={14} />, onSelect: () => c().setTextAlign("center").run() },
            { label: "Right-align", icon: <AlignRight size={14} />, onSelect: () => c().setTextAlign("right").run() },
          ]}
        />
      )}
      {insert.anchor && <InsertMenu anchor={insert.anchor} editor={editor} env={env} onClose={insert.close} />}
      {style.anchor && (
        <Menu
          anchor={style.anchor}
          onClose={style.close}
          entries={[
            { label: "Large header", checked: s.h === 1, hint: "Ctrl+Alt+1", onSelect: () => c().setHeading({ level: 1 }).run() },
            { label: "Medium header", checked: s.h === 2, hint: "Ctrl+Alt+2", onSelect: () => c().setHeading({ level: 2 }).run() },
            { label: "Small header", checked: s.h === 3, hint: "Ctrl+Alt+3", onSelect: () => c().setHeading({ level: 3 }).run() },
            { label: "Extra small header", checked: s.h === 4, onSelect: () => c().setHeading({ level: 4 }).run() },
            { label: "Normal text", checked: !s.h && !s.codeBlock, hint: "Ctrl+Alt+0", onSelect: () => c().setParagraph().run() },
            { label: "Code block", checked: s.codeBlock, hint: "Ctrl+Shift+L", onSelect: () => c().toggleCodeBlock().run() },
          ]}
        />
      )}
      {font.anchor && (
        <Menu
          anchor={font.anchor}
          onClose={font.close}
          entries={FONTS.map(([label, css]) => ({
            label,
            checked: fontLabel === label,
            onSelect: () => (css ? c().setFontFamily(css).run() : c().unsetFontFamily().run()),
          }))}
        />
      )}
      {size.anchor && (
        <Popover anchor={size.anchor} onClose={size.close} className="menu size-menu">
          {SIZES.map((px) => (
            <button
              key={px}
              className="menu-item"
              onClick={() => {
                size.close();
                if (px === 15) c().unsetFontSize().run();
                else c().setFontSize(`${px}px`).run();
              }}
            >
              <span className="menu-label">{px}</span>
              {String(px) === sizeLabel && <span className="menu-check">✓</span>}
            </button>
          ))}
        </Popover>
      )}
      {color.anchor && (
        <Popover anchor={color.anchor} onClose={color.close} className="menu color-menu">
          <div className="menu-heading">Font color</div>
          <div className="swatches">
            <button className="swatch auto" title="Automatic" onClick={() => { c().unsetColor().run(); color.close(); }}>A</button>
            {TEXT_COLORS.map((col) => (
              <button key={col} className="swatch" style={{ color: col }} title={col} onClick={() => { c().setColor(col).run(); color.close(); }}>A</button>
            ))}
          </div>
          <div className="menu-heading">Highlight</div>
          <div className="swatches">
            <button className="swatch auto" title="None" onClick={() => { c().unsetHighlight().run(); color.close(); }}>⌀</button>
            {HIGHLIGHTS.map((col) => (
              <button key={col} className="swatch hl" style={{ background: col }} title={col} onClick={() => { c().setHighlight({ color: col }).run(); color.close(); }} />
            ))}
          </div>
        </Popover>
      )}
      {more.anchor && (
        <Menu
          anchor={more.anchor}
          align="end"
          onClose={more.close}
          entries={[
            // Evernote's More: whatever the toolbar had no room for, then alignment, indents, the rarer marks.
            ...overflow,
            { separator: true as const },
            { label: "Left-align", icon: <AlignLeft size={14} />, hint: "Ctrl+L", onSelect: () => c().setTextAlign("left").run() },
            { label: "Center-align", icon: <AlignCenter size={14} />, hint: "Ctrl+E", onSelect: () => c().setTextAlign("center").run() },
            { label: "Right-align", icon: <AlignRight size={14} />, hint: "Ctrl+J", onSelect: () => c().setTextAlign("right").run() },
            { label: "Indent", icon: <IndentIncrease size={14} />, hint: "Tab", onSelect: () => (editor.commands.focus(), indentOrSink(editor, 1)) },
            { label: "Outdent", icon: <IndentDecrease size={14} />, hint: "Shift+Tab", onSelect: () => (editor.commands.focus(), indentOrSink(editor, -1)) },
            { separator: true },
            { label: "Strikethrough", icon: <Strikethrough size={14} />, checked: s.strike, hint: "Ctrl+Shift+S", onSelect: () => c().toggleStrike().run() },
            { label: "Superscript", icon: <SupIcon size={14} />, onSelect: () => c().toggleSuperscript().run() },
            { label: "Subscript", icon: <SubIcon size={14} />, onSelect: () => c().toggleSubscript().run() },
            { separator: true },
            { label: "Encrypt selected text…", icon: <Lock size={14} />, disabled: editor.state.selection.empty, onSelect: () => encryptSelection(editor) },
            { separator: true },
            { label: "Simplify formatting", icon: <Eraser size={14} />, hint: "Ctrl+Space", onSelect: () => c().unsetColor().unsetHighlight().unsetTextAlign().run() },
            { label: "Remove formatting", icon: <RemoveFormatting size={14} />, onSelect: () => c().unsetAllMarks().clearNodes().run() },
          ]}
        />
      )}
      {linkPrompt && (
        <Prompt
          title={s.link ? "Edit link" : "Insert link"}
          label="URL"
          initial={(editor.getAttributes("link").href as string) ?? ""}
          confirm="Apply"
          onSubmit={(url) => {
            const href = /^(https?:|mailto:|tel:|\/note\/)/i.test(url) ? url : `https://${url}`;
            if (editor.state.selection.empty && !s.link) c().insertContent({ type: "text", text: url, marks: [{ type: "link", attrs: { href } }] }).run();
            else c().extendMarkRange("link").setLink({ href }).run();
          }}
          onClose={() => setLinkPrompt(false)}
        />
      )}
    </div>
  );
}

function TB({ title, active, disabled, opt, onClick, children }: { title: string; active?: boolean; disabled?: boolean; opt?: string; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; children: React.ReactNode }) {
  return (
    <button className={`tb-btn${active ? " active" : ""}`} data-opt={opt} title={title} aria-label={title} aria-pressed={active} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}
